/**
 * /news/:slug/embed -- so' a noticia, pra WebView do jogo (popup "Ultimas Noticias" do PC e do Android, dono
 * 2026-10-22): mesma busca e o mesmo artigo do NextNewsDetailPage, sem cabecalho/rodape, sem "Voltar as noticias" e
 * sem "Mais noticias" (o "Voltar" fica no jogo). Links da noticia abrem no navegador do jogador (o jogo manda pra fora
 * o que nao e' do site).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Newspaper } from 'lucide-react';
import { getNewsBySlug, type NewsItem } from '@/shared/services/eventsService';
import { CATEGORY_LABEL, newsCategoryOf, newsDate, newsPlainTitle } from '@/features/Home/next/newsData';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';
import '@/features/Home/next/news.css';
import './news-pages.css';

export default function NextNewsEmbedPage() {
  const { id: slug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // fundo e barra de rolagem do documento no tema (a WebView rola o <html>)
  useEffect(() => {
    document.documentElement.classList.add('nxp-embed-doc');
    return () => document.documentElement.classList.remove('nxp-embed-doc');
  }, []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getNewsBySlug(slug)
      .then((r) => {
        if (cancelled) return;
        setItem(r);
        if (r.slug && r.slug !== slug) navigate(`/news/${r.slug}/embed`, { replace: true });   // link antigo (id)
      })
      .catch((e: { Message?: string; message?: string }) => {
        if (!cancelled) setError(e?.Message || e?.message || 'Notícia não encontrada.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  useEffect(() => {
    document.title = item ? `${newsPlainTitle(item.title)} · Cabal Next` : 'Notícias · Cabal Next';
  }, [item]);

  const category = item ? newsCategoryOf(item.title) : 'noticia';

  return (
    <div className="nx nxp-embed">
      {loading && (
        <div className="nxp-loading">
          <Loader2 size={30} className="nxp-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="nxp-error">
          <Newspaper size={38} />
          <h1>{error}</h1>
          <p>Talvez ela tenha sido removida.</p>
        </div>
      )}

      {!loading && !error && item && (
        <article className="nxp-article">
          {item.image && (
            <div className="nxp-article__cover">
              <img src={newsImg(item.image, 1280)} onError={newsImgFallback(item.image)} alt={item.title} />
            </div>
          )}
          <div className="nxp-article__body">
            <span className="nxn-meta">
              <span className={`nxn-chip nxn-chip--${category}`}>{CATEGORY_LABEL[category]}</span>
              <span className="nxn-chip nxn-chip--date">{newsDate(item.createdDate)}</span>
            </span>
            <h1>{newsPlainTitle(item.title)}</h1>
            {item.subTitle && <p className="nxp-article__sub">{item.subTitle}</p>}
            <div className="nxp-html" dangerouslySetInnerHTML={{ __html: item.html || '' }} />
          </div>
        </article>
      )}
    </div>
  );
}
